// src/pages/Leads.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import useOrgSettings from '../hooks/useOrgSettings';

// Extracted components
import CancelModal from './leads/CancelModal';
import DncModal from './leads/DncModal';
import FiltersBar from './leads/FiltersBar';
import LeadRow from './leads/LeadRow';
import Pagination from './leads/Pagination';

// Constants (moved out)
import {
  STATUS,
  PAGE_SIZE,
  SORTS,
  SALE_CANCEL_REASONS,
  DNC_REASONS,
  ACTIVE_STATUSES_DEFAULT,
} from './leads/constants';

// Local rule helpers (kept here)
const APPOINTMENT_CANCEL_ALLOWED = new Set(['NEW', 'ASSIGNED', 'REAPP', 'PTA']);
const isSaleCancelAllowed = (s) => (s || '').toUpperCase() === 'SOLD';

export default function Leads() {
  const toast = useToast();
  const navigate = useNavigate();
  const { orgId } = useOrgSettings();

  const functions = useMemo(() => getFunctions(undefined, 'europe-west2'), []);
  const fnUnassign = useMemo(() => httpsCallable(functions, 'adminUnassignLead'), [functions]);
  const fnReinstate = useMemo(() => httpsCallable(functions, 'adminReinstateLead'), [functions]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterSeller, setFilterSeller] = useState('ALL');
  const [salespeople, setSalespeople] = useState([]);

  const [searchText, setSearchText] = useState('');
  const [sortId, setSortId] = useState('DATE_DESC');

  // pagination state
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const cursors = useRef([]); // keep last doc of each page for Prev/Next

  // Modal state for cancellation
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLead, setCancelLead] = useState(null); // full lead object
  const [cancelType, setCancelType] = useState('appointment'); // 'appointment' | 'sale'
  const [reasonPreset, setReasonPreset] = useState(''); // used only for sale
  const [reasonText, setReasonText] = useState(''); // free text (required for appointment; required when sale->Other)
  const [submitting, setSubmitting] = useState(false);

  const [hideFinished, setHideFinished] = useState(true);

  // DNC modal state
  const [dncOpen, setDncOpen] = useState(false);
  const [dncLead, setDncLead] = useState(null);
  const [dncReason, setDncReason] = useState('Do Not Contact');
  const [dncNote, setDncNote] = useState('');

  // ===== Effects =====

  // live salespeople list (for filter + display)
  useEffect(() => {
    if (!orgId) return;
    const unsub = onSnapshot(
      collection(db, `organizations/${orgId}/salespeople`),
      (snap) => setSalespeople(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('[Leads] salespeople listen error:', err)
    );
    return () => unsub();
  }, [orgId]);

  // LIVE leads query (filters/sort/pagination)
  useEffect(() => {
    if (!orgId) return;

    // build the query
const sort = SORTS.find((s) => s.id === sortId) || SORTS[0];
const clauses = [];

// Hide Call-Again by default when Status filter is "ALL"
if (filterStatus === 'ALL') {
  clauses.push(where('status', 'in', ACTIVE_STATUSES_DEFAULT));
} else {
  clauses.push(where('status', '==', filterStatus));
}

if (filterSeller !== 'ALL') {
  clauses.push(where('salespersonId', '==', filterSeller));
}

clauses.push(orderBy(sort.field, sort.dir));

if (page > 1 && cursors.current[page - 2]) {
  clauses.push(startAfter(cursors.current[page - 2]));
}

clauses.push(limit(PAGE_SIZE + 1));

    const q = query(collection(db, `organizations/${orgId}/leads`), ...clauses);

    setLoading(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        // Rebuild from the snapshot every time so removed docs disappear immediately
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data(), __snap: d }));
        const pageDocs = docs.slice(0, PAGE_SIZE);

        setRows(pageDocs);
        setHasNext(docs.length > PAGE_SIZE);

        // keep cursor for this page (so Next/Prev continues to work)
        cursors.current[page - 1] = pageDocs.length
          ? pageDocs[pageDocs.length - 1].__snap
          : null;

        // If the file already had setLoading(false) after snapshot, keep it here:
        setLoading(false);
      },
      (e) => {
        console.error('[Leads] live query error:', e);
        const needsIndex = /index/i.test(e?.message || '');
        toast({
          title: needsIndex ? 'A Firestore index is required' : 'Error loading leads',
          description: needsIndex
            ? 'Open the browser console and click the Firestore link to create the index once. After it builds, sorting will work automatically.'
            : e.message,
          status: 'error',
          isClosable: true,
        });
        setLoading(false);
      }
    );

    // cleanup on deps change
    return () => unsub();
  }, [orgId, filterStatus, filterSeller, sortId, page, toast]);

  // ===== Helpers (component scope) =====

  const resetCancelModal = () => {
    setCancelOpen(false);
    setCancelLead(null);
    setCancelType('appointment');
    setReasonPreset('');
    setReasonText('');
    setSubmitting(false);
  };

  const resetDncModal = () => {
    setDncOpen(false);
    setDncLead(null);
    setDncReason('Do Not Contact');
    setDncNote('');
  };

  const resetAndRefetch = () => {
    setPage(1);
    cursors.current = [];
  };

  // Unassign a lead and set status back to NEW
  const handleUnassign = async (lead) => {
    try {
      await fnUnassign({ orgId, leadId: lead.id, setStatus: 'NEW', reason: '' });
      toast({ title: 'Lead unassigned', status: 'success', isClosable: true });
      // Live query will refresh the table automatically
    } catch (e) {
      console.error(e);
      toast({
        title: 'Unassign failed',
        description: e.message,
        status: 'error',
        isClosable: true,
      });
    }
  };

  // === DNC flow ===
  const openDncModal = (lead) => {
    setDncLead(lead);
    setDncReason('Do Not Contact');
    setDncNote('');
    setDncOpen(true);
  };

  const submitDnc = async () => {
    if (!dncLead) return;
    try {
      const leadRef = doc(db, 'organizations', orgId, 'leads', dncLead.id);
      const snap = await getDoc(leadRef);
      const lead = snap.data() || {};
      const prevStatus = (lead.status || '').toUpperCase();
      const history = Array.isArray(lead.statusHistory) ? [...lead.statusHistory] : [];
      history.push({
        at: new Date().toISOString(),
        byUid: null,
        from: prevStatus,
        to: 'ARCHIVE_DNC',
      });

      await updateDoc(leadRef, {
        status: 'ARCHIVE_DNC',
        doNotContact: true,
        dncReason: dncReason || 'Do Not Contact',
        dncNote: (dncNote || '').trim(),
        dncAt: serverTimestamp(),
        dncBy: { uid: null, name: 'Admin' },
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
        statusHistory: history,
      });

      toast({ title: 'Marked as Do Not Contact', status: 'success', isClosable: true });
      resetDncModal();
      // live query filtering will hide it automatically
    } catch (e) {
      console.error(e);
      toast({
        title: 'Failed to mark as DNC',
        description: e.message,
        status: 'error',
        isClosable: true,
      });
    }
  };

  // === Cancel flow ===
  const openCancelModal = (lead, type) => {
    setCancelLead(lead);
    setCancelType(type); // 'appointment' or 'sale'
    setCancelOpen(true);
  };

  const submitCancellation = async () => {
    if (!cancelLead) return;

    // Build final reason based on type:
    //  - appointment: free-text required
    //  - sale: preset required; if "Other", free-text required
    let finalReason = '';

    if (cancelType === 'appointment') {
      finalReason = (reasonText || '').trim();
      if (!finalReason) {
        toast({
          title: 'Notes required',
          description: 'Please type why the appointment is cancelled.',
          status: 'warning',
          isClosable: true,
        });
        return;
      }
    } else {
      if (!reasonPreset) {
        toast({
          title: 'Reason required',
          description: 'Please choose a preset reason.',
          status: 'warning',
          isClosable: true,
        });
        return;
      }
      finalReason = reasonPreset === 'Other' ? (reasonText || '').trim() : reasonPreset;
      if (!finalReason) {
        toast({
          title: 'Reason required',
          description: 'Please type a reason for “Other”.',
          status: 'warning',
          isClosable: true,
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      if (cancelType === 'appointment') {
        // appointment cancel → unassign and set status CPTA
        await fnUnassign({ orgId, leadId: cancelLead.id, setStatus: 'CPTA', reason: finalReason });
        toast({ title: 'Appointment cancelled (CPTA)', status: 'success', isClosable: true });
      } else {
        // sale cancel → mark as ARCHIVE_DNC (hard stop)
        const leadRef = doc(db, 'organizations', orgId, 'leads', cancelLead.id);
        const snap = await getDoc(leadRef);
        const lead = snap.data() || {};
        const prevStatus = (lead.status || '').toUpperCase();
        const history = Array.isArray(lead.statusHistory) ? [...lead.statusHistory] : [];
        history.push({
          at: new Date().toISOString(),
          byUid: null,
          from: prevStatus,
          to: 'ARCHIVE_DNC',
        });
        await updateDoc(leadRef, {
          status: 'ARCHIVE_DNC',
          doNotContact: true,
          dncReason: finalReason,
          dncNote: '',
          dncAt: serverTimestamp(),
          dncBy: { uid: null, name: 'Admin' },
          updatedAt: serverTimestamp(),
          lastActivityAt: serverTimestamp(),
          statusHistory: history,
        });
        toast({
          title: 'Sale cancelled — lead archived as DNC',
          status: 'success',
          isClosable: true,
        });
      }
      resetCancelModal();
    } catch (e) {
      console.error(e);
      toast({
        title: 'Cancellation failed',
        description: e.message,
        status: 'error',
        isClosable: true,
      });
      setSubmitting(false);
    }
  };

  const handleReinstate = async (lead) => {
    try {
      await fnReinstate({ orgId, leadId: lead.id });
      toast({ title: 'Lead reinstated to NEW', status: 'success', isClosable: true });
    } catch (e) {
      console.error(e);
      toast({
        title: 'Reinstate failed',
        description: e.message,
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleEditCust = (lead) => {
    if (lead.salespersonId) {
      toast({
        title: 'Unassign before editing',
        description: `This lead is currently assigned to ${lead.salespersonName || 'a sales rep'}. Unassign it first to edit customer details.`,
        status: 'info',
        isClosable: true,
      });
      return;
    }
    navigate(`/leads/${lead.id}/edit-customer`);
  };

  const showCancelAppointment = (lead) =>
    APPOINTMENT_CANCEL_ALLOWED.has((lead.status || '').toUpperCase());
  const showCancelSale = (lead) => isSaleCancelAllowed(lead.status);
  const showReinstate = (lead) => (lead.status || '').toUpperCase() === 'CANCELLED'; // legacy rows only

  // Handlers passed to FiltersBar (keeps reset logic centralised)
  const handleChangeStatus = (value) => {
    setFilterStatus(value);
    resetAndRefetch();
  };
  const handleChangeSeller = (value) => {
    setFilterSeller(value);
    resetAndRefetch();
  };
  const handleChangeSort = (value) => {
    setSortId(value);
    resetAndRefetch();
  };

  // ===== Render =====
  return (
    <Box borderWidth={1} borderRadius="lg" p={6} boxShadow="lg" bg="white">
      <Heading size="xl" mb={2} color="blue.600">
        Leads
      </Heading>
      <Text fontSize="md" color="gray.600" mb={6}>
        Create, find, and manage leads in one live dashboard. Use the “Actions” menu to assign,
        unassign, edit customer details, cancel, and mark as DNC with just a few clicks.
      </Text>

      <VStack align="stretch" spacing={6}>
        {/* Filters / Toolbar */}
        <FiltersBar
          filterStatus={filterStatus}
          onChangeStatus={handleChangeStatus}
          filterSeller={filterSeller}
          onChangeSeller={handleChangeSeller}
          salespeople={salespeople}
          sortId={sortId}
          onChangeSort={handleChangeSort}
          searchText={searchText}
          onChangeSearch={setSearchText}
          hideFinished={hideFinished}
          onToggleHideFinished={setHideFinished}
          statusOptions={STATUS}
          sortOptions={SORTS}
          onNewLead={() => navigate('/leads/new')}
        />

        {loading ? (
          <VStack align="start" spacing={4}>
            <Spinner size="lg" />
            <Text fontSize="lg">Loading…</Text>
          </VStack>
        ) : (
          <>
            <Box overflowX="auto" width="100%">
              <Table size="sm" variant="simple" minWidth="1000px">
                <Thead>
                  <Tr>
                    <Th>Customer</Th>
                    <Th>Phone</Th>
                    <Th>Postcode</Th>
                    <Th>Status</Th>
                    <Th>STAGE</Th>
                    <Th>Sales Rep</Th>
                    <Th>Appointment Date</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows
                    .filter((r) => {
                      // Hide Finished toggle
                      if (hideFinished && (r.apptEndAt || (r.status || '').toUpperCase() === 'FINISHED')) {
                        return false;
                      }
                      // HIDE ARCHIVE_DNC entirely from Leads list
                      if ((r.status || '').toUpperCase() === 'ARCHIVE_DNC') return false;

                      // Client-side search
                      const q = (searchText || '').trim().toLowerCase();
                      if (!q) return true;
                      const seller = r.salespersonName || '';
                      const name =
                        r.customerFullName ||
                        `${r.customerFirstName || ''} ${r.customerLastName || ''}`.trim();
                      return (
                        name.toLowerCase().includes(q) ||
                        (r.postcode || '').toLowerCase().includes(q) ||
                        (r.status || '').toLowerCase().includes(q) ||
                        seller.toLowerCase().includes(q) ||
                        (r.customerPhone || '').toLowerCase().includes(q)
                      );
                    })
                    .map((lead) => (
                      <LeadRow
                        key={lead.id}
                        lead={lead}
                        onEditCustomer={() => handleEditCust(lead)}
                        onAssign={() => navigate(`/leads/${lead.id}/assign`)}
                        onUnassign={() => handleUnassign(lead)}
                        onOpenDnc={() => openDncModal(lead)}
                        onOpenCancelAppt={() => openCancelModal(lead, 'appointment')}
                        onOpenCancelSale={() => openCancelModal(lead, 'sale')}
                        onReinstate={() => handleReinstate(lead)}
                        canCancelAppt={showCancelAppointment(lead)}
                        canCancelSale={showCancelSale(lead)}
                        canReinstate={showReinstate(lead)}
                      />
                    ))}
                </Tbody>
              </Table>
            </Box>

            {/* Pagination (extracted) */}
            <Pagination
              page={page}
              hasNext={hasNext}
              isLoading={loading}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => p + 1)}
            />

            <Box mt={2}>
              <Text fontSize="xs" color="gray.500">
                Note: Fixihub acts as a neutral venue. We do not decide what customer information is collected or how it is used.
                Your organisation (the company using Fixihub) is the data controller for any personal data you input.
              </Text>
            </Box>
          </>
        )}
      </VStack>

      {/* Extracted Modals */}
      <CancelModal
        isOpen={cancelOpen}
        onClose={resetCancelModal}
        cancelType={cancelType}
        saleReasons={SALE_CANCEL_REASONS}
        reasonPreset={reasonPreset}
        onChangeReasonPreset={setReasonPreset}
        reasonText={reasonText}
        onChangeReasonText={setReasonText}
        onSubmit={submitCancellation}
        submitting={submitting}
      />

      <DncModal
        isOpen={dncOpen}
        onClose={resetDncModal}
        dncReasons={DNC_REASONS}
        dncReason={dncReason}
        onChangeDncReason={setDncReason}
        dncNote={dncNote}
        onChangeDncNote={setDncNote}
        onSubmit={submitDnc}
      />
    </Box>
  );
}